import { useEffect, useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { readQcReport, type QcReportRow } from "@/lib/manufacturing/builderAssistantChecklistClient";

export function BuilderAssistantQcReport({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const [header, setHeader] = useState({ serial: "", plant: "", customer: "", model: "", signoff: "" });
  const [rows, setRows] = useState<QcReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);

  useEffect(() => {
    void readQcReport(sessionId).then((res) => {
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setHeader({
        serial: res.data.report.serial,
        plant: res.data.report.plant,
        customer: res.data.report.customer,
        model: res.data.report.model,
        signoff: res.data.report.signoff ?? "",
      });
      setRows(res.data.report.rows);
    });
  }, [sessionId]);

  const fails = rows.filter((row) => row.result === "fail");
  const passes = rows.filter((row) => row.result === "pass");
  const meta = [header.serial, header.plant, header.customer, header.model].filter(Boolean).join(" · ");

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">QC report</h3>
          <p className="text-sm text-gray-600 dark:text-zinc-400">{meta || "Inspection"}</p>
          {header.signoff ? <p className="text-sm text-green-800 dark:text-green-200">{header.signoff}</p> : null}
        </div>
        <button type="button" className={ghostBtnClass} onClick={onClose}>Back</button>
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Failures</h4>
        {fails.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">No failed items.</p>
        ) : (
          <ol className="mt-2 list-decimal space-y-3 pl-5 text-sm">
            {fails.map((row) => (
              <li key={`${row.category}-${row.title}`}>
                <p className="font-medium text-gray-900 dark:text-zinc-100">{row.category} — {row.title}</p>
                {row.note ? <p className="text-gray-700 dark:text-zinc-300">{row.note}</p> : null}
                {row.photos.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {row.photos.map((url) => (
                      <button key={url} type="button" onClick={() => setPhoto(url)}>
                        <img src={url} alt="" className="h-16 w-16 rounded object-cover" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Passed</h4>
        {passes.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">No passed items.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm text-gray-800 dark:text-zinc-200">
            {passes.map((row) => (
              <li key={`${row.category}-${row.title}`}>{row.category} — {row.title}</li>
            ))}
          </ul>
        )}
      </div>
      {photo ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog">
          <button type="button" className="absolute right-4 top-4 text-sm text-white" onClick={() => setPhoto(null)}>
            Close
          </button>
          <img src={photo} alt="" className="max-h-[85vh] max-w-full rounded" />
        </div>
      ) : null}
    </section>
  );
}
